import { useQuery } from '@tanstack/react-query';
import { Bell, Building2, Target, DollarSign, Home, Inbox, KanbanSquare, LogOut, Menu, Search, Settings, Users, Briefcase } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { config } from '../config';
import { supabase } from '../lib/supabase';
import { CheckOffProvider } from './CheckOff';
import { ToastProvider } from './Toast';
import { cx } from './ui';

function useCounts() {
  return useQuery({
    queryKey: ['nav-counts'],
    queryFn: async () => {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: config.timezone });
      const [review, due] = await Promise.all([
        supabase.from('review_queue').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('reminders').select('id', { count: 'exact', head: true }).eq('status', 'open').lte('due_date', today),
      ]);
      return { review: review.count ?? 0, due: due.count ?? 0 };
    },
    refetchInterval: 60_000,
  });
}

export function Layout() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: counts } = useCounts();

  const nav = [
    { to: '/', label: 'Home', icon: Home, end: true },
    { to: '/contacts', label: 'Contacts', icon: Users },
    { to: '/companies', label: 'Companies', icon: Briefcase },
    { to: '/buildings', label: 'Buildings', icon: Building2 },
    { to: '/pipeline', label: 'Pipeline', icon: KanbanSquare },
    { to: '/revenue', label: 'Revenue', icon: DollarSign },
    { to: '/goals', label: 'Goals', icon: Target },
    { to: '/reminders', label: 'Reminders', icon: Bell, badge: counts?.due },
    { to: '/review', label: 'Review', icon: Inbox, badge: counts?.review },
    { to: '/settings', label: 'Settings', icon: Settings },
  ];

  function onSearch(e: FormEvent) {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setMenuOpen(false);
  }

  const links = (
    <nav className="flex flex-col gap-0.5">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            cx(
              'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium',
              isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          <n.icon className="h-4 w-4" />
          <span className="flex-1">{n.label}</span>
          {!!n.badge && <span className="rounded-full bg-rose-500 px-1.5 text-xs font-semibold text-white">{n.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );

  const searchBox = (
    <form onSubmit={onSearch} className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search everything…"
        className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-2 text-sm focus:border-indigo-500 focus:bg-white focus:outline-none"
      />
    </form>
  );

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden w-56 shrink-0 flex-col gap-4 border-r border-slate-200 bg-white px-3 py-4 lg:flex lg:sticky lg:top-0 lg:h-screen">
        <div className="px-2 text-lg font-bold tracking-tight text-indigo-700">{config.appName}</div>
        {searchBox}
        {links}
        <button
          onClick={() => supabase.auth.signOut()}
          className="mt-auto flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-slate-500 hover:bg-slate-100"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>

      {/* Phone / narrow header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white px-3 py-2 lg:hidden">
        <div className="flex items-center gap-2">
          <button onClick={() => setMenuOpen((o) => !o)} className="rounded p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Menu">
            <Menu className="h-5 w-5" />
          </button>
          <div className="font-bold text-indigo-700">{config.appName}</div>
          <div className="ml-auto w-44">{searchBox}</div>
        </div>
        {menuOpen && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            {links}
            <button onClick={() => supabase.auth.signOut()} className="mt-1 flex items-center gap-2.5 px-2.5 py-1.5 text-sm text-slate-500">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
      </header>

      <main className="min-w-0 flex-1 px-4 pb-24 pt-5 sm:px-6 lg:px-8">
        <ToastProvider>
          <CheckOffProvider>
            <Outlet />
          </CheckOffProvider>
        </ToastProvider>
      </main>
    </div>
  );
}
